/**
 * R-MAIL-006 — sanitize untrusted email HTML for reader.
 * Remote content blocked by default; CID + remote opt-in via data-* hooks.
 *
 * Strategy: DOM pre-pass marks/strips every remote media form (src, srcset,
 * picture/source, CSS url(), lazy data-*), then DOMPurify cleans XSS.
 * Count uses data-mail-remote(s) so the C-22 strip always appears when needed.
 */
import createDOMPurify from 'dompurify'

type Purify = ReturnType<typeof createDOMPurify>

const REMOTE_URL = /^(https?:)?\/\//i

const MEDIA_ATTRS = [
  'src',
  'srcset',
  'xlink:href',
  'poster',
  'background',
  'data-src',
  'data-original',
  'data-lazy-src',
  'data-bg',
  'data-background',
  'data-image',
  'data-srcset',
  'data-lazy-srcset',
] as const

const STYLE_URL_RE = /url\s*\(\s*(['"]?)(\s*(?:https?:)?\/\/[^'")\s]+)\1\s*\)/gi

let purify: Purify | null = null

function getPurify(): Purify {
  if (typeof window === 'undefined') {
    throw new Error('sanitizeEmailHtml requires a DOM (window)')
  }
  if (!purify) purify = createDOMPurify(window)
  return purify
}

export function isRemoteUrl(value: string | null): boolean {
  if (!value) return false
  const v = value.trim()
  if (v.startsWith('data:') || v.startsWith('cid:') || v.startsWith('blob:')) return false
  return REMOTE_URL.test(v) || v.startsWith('//')
}

export function parseCid(value: string): string | null {
  const v = value.trim()
  if (!/^cid:/i.test(v)) return null
  return v.replace(/^cid:/i, '').replace(/^<|>$/g, '').trim() || null
}

export function normalizeRemoteUrl(value: string): string {
  const v = value.trim()
  if (v.startsWith('//')) return `https:${v}`
  return v
}

export function parseSrcsetUrls(srcset: string): string[] {
  const out: string[] = []
  for (const part of srcset.split(',')) {
    const url = part.trim().split(/\s+/)[0]
    if (url) out.push(url)
  }
  return out
}

export function extractStyleUrls(style: string): string[] {
  const out: string[] = []
  const re = new RegExp(STYLE_URL_RE.source, 'gi')
  let m: RegExpExecArray | null
  while ((m = re.exec(style))) {
    out.push(m[2].trim())
  }
  return out
}

export function extractRemoteMediaUrls(html: string): string[] {
  if (!html) return []
  const seen = new Set<string>()
  const add = (raw: string) => {
    if (!isRemoteUrl(raw)) return
    seen.add(normalizeRemoteUrl(raw))
  }
  for (const m of html.matchAll(
    /\b(?:src|poster|background|data-src|data-original|data-lazy-src|data-bg|data-background|data-image)\s*=\s*["']([^"']+)["']/gi,
  )) {
    add(m[1])
  }
  for (const m of html.matchAll(/\b(?:srcset|data-srcset|data-lazy-srcset)\s*=\s*["']([^"']+)["']/gi)) {
    for (const u of parseSrcsetUrls(m[1])) add(u)
  }
  for (const u of extractStyleUrls(html)) add(u)
  return [...seen]
}

function markBlockedRemote(el: Element, url: string) {
  const abs = normalizeRemoteUrl(url)
  if (!isRemoteUrl(abs)) return
  const prev = el.getAttribute('data-mail-remotes') || ''
  const list = prev ? prev.split('\n').filter(Boolean) : []
  if (!list.includes(abs)) list.push(abs)
  el.setAttribute('data-mail-remotes', list.join('\n'))
  if (!el.getAttribute('data-mail-remote')) {
    el.setAttribute('data-mail-remote', abs)
  }
  if (el.tagName === 'IMG' || el.tagName === 'SOURCE') {
    el.setAttribute('alt', el.getAttribute('alt') || 'blocked image')
  }
}

/** Strip remote media and mark nodes before XSS sanitize. */
function prepassBlockRemote(root: ParentNode, allowRemote: boolean) {
  if (allowRemote) return
  const elements = root.querySelectorAll('*')
  for (const el of Array.from(elements)) {
    for (const name of MEDIA_ATTRS) {
      if (!el.hasAttribute(name)) continue
      const val = el.getAttribute(name) || ''
      if (name === 'srcset' || name === 'data-srcset' || name === 'data-lazy-srcset') {
        const urls = parseSrcsetUrls(val).filter((u) => isRemoteUrl(u))
        if (urls.length) {
          for (const u of urls) markBlockedRemote(el, u)
          el.removeAttribute(name)
        }
        continue
      }
      const cid = parseCid(val)
      if (cid) {
        el.removeAttribute(name)
        el.setAttribute('data-mail-cid', cid)
        el.setAttribute('alt', el.getAttribute('alt') || 'inline image')
        continue
      }
      if (isRemoteUrl(val)) {
        markBlockedRemote(el, val)
        el.removeAttribute(name)
      }
    }
    const style = el.getAttribute('style')
    if (style) {
      // Should be rare after parkStyles; never write style= back (parent CSP).
      const urls = extractStyleUrls(style).filter((u) => isRemoteUrl(u))
      if (urls.length) {
        for (const u of urls) markBlockedRemote(el, u)
      }
      el.removeAttribute('style')
    }
  }
}

function alsoBlockJavascriptHref(root: ParentNode) {
  for (const el of Array.from(root.querySelectorAll('[href]'))) {
    const href = el.getAttribute('href') || ''
    if (/^\s*javascript:/i.test(href)) el.removeAttribute('href')
  }
}

/** Keep the mail client open — external links always open in a new tab. */
export function forceExternalLinksNewTab(root: ParentNode) {
  for (const el of Array.from(root.querySelectorAll('a[href]'))) {
    const href = (el.getAttribute('href') || '').trim()
    if (!href || href.startsWith('#') || /^\s*javascript:/i.test(href)) continue
    // http(s), protocol-relative, mailto, tel — never navigate the SPA away
    if (/^(https?:|mailto:|tel:)/i.test(href) || href.startsWith('//')) {
      el.setAttribute('target', '_blank')
      const prev = (el.getAttribute('rel') || '').toLowerCase().split(/\s+/).filter(Boolean)
      for (const token of ['noopener', 'noreferrer']) {
        if (!prev.includes(token)) prev.push(token)
      }
      el.setAttribute('rel', prev.join(' '))
    }
  }
}

/** String-safe variant — avoids DOM round-trip that can drop parked attrs. */
export function forceExternalLinksNewTabInHtml(html: string): string {
  if (!html || !/<a\b/i.test(html)) return html
  return html.replace(/<a\b([^>]*)>/gi, (full, attrs: string) => {
    if (!/\bhref\s*=/i.test(attrs)) return full
    const hm = attrs.match(/\bhref\s*=\s*(["'])([\s\S]*?)\1/i)
    if (!hm) return full
    const href = hm[2].trim()
    if (!href || href.startsWith('#') || /^\s*javascript:/i.test(href)) return full
    if (!(/^(https?:|mailto:|tel:)/i.test(href) || href.startsWith('//'))) return full
    let next = attrs
    if (/\btarget\s*=/i.test(next)) {
      next = next.replace(/\btarget\s*=\s*(["'])[\s\S]*?\1/i, 'target="_blank"')
    } else {
      next += ' target="_blank"'
    }
    if (/\brel\s*=/i.test(next)) {
      next = next.replace(/\brel\s*=\s*(["'])([\s\S]*?)\1/i, (_m, q: string, rel: string) => {
        const parts = new Set(rel.toLowerCase().split(/\s+/).filter(Boolean))
        parts.add('noopener')
        parts.add('noreferrer')
        return `rel=${q}${[...parts].join(' ')}${q}`
      })
    } else {
      next += ' rel="noopener noreferrer"'
    }
    return `<a${next}>`
  })
}

/** Strip fixed pixel widths from newsletter tables/images so CSS max-width:100% can shrink
 * them on narrow viewports (Gmail-like; WCAG 1.4.10).
 */
export function constrainFixedLayoutWidths(root: ParentNode) {
  for (const el of Array.from(root.querySelectorAll('table, img, td, th'))) {
    const w = (el.getAttribute('width') || '').trim()
    if (/^\d+px$/i.test(w)) {
      el.removeAttribute('width')
    } else if (/^\d+$/.test(w) && Number(w) > 100) {
      // Bare HTML width >100 is pixels (percent values use %)
      el.removeAttribute('width')
    }
    let style = el.getAttribute('style') || ''
    if (!style) continue
    // Parked path avoids this; if a style= slipped through, drop it (CSP) after
    // extracting width clamps into a data attr for unpark-less fallback.
    let next = style
      .replace(/(?:^|;)\s*width\s*:\s*\d{3,}px\s*/gi, ';')
      .replace(/(?:^|;)\s*min-width\s*:\s*\d{3,}px\s*/gi, ';min-width:0')
      .replace(/;;+/g, ';')
      .replace(/^;|;$/g, '')
      .trim()
    if (/width\s*:\s*\d{3,}px/i.test(style) && !/max-width\s*:/i.test(next)) {
      next = next ? `${next};max-width:100%` : 'max-width:100%'
    }
    el.removeAttribute('style')
    if (next) el.setAttribute('data-mail-parked-attr-fallback', next)
  }
}

/** Sanitize CSS text (style tag body) without touching the live DOM. */
function sanitizeCssText(css: string, allowRemote: boolean): string {
  let out = css
  out = out.replace(/@import[\s\S]*?;/gi, '/* import blocked */')
  out = out.replace(/expression\s*\(/gi, 'blocked(')
  out = out.replace(/-moz-binding\s*:/gi, 'blocked:')
  out = out.replace(/behavior\s*:/gi, 'blocked:')
  if (!allowRemote) {
    out = out.replace(/url\s*\(\s*(['"]?)https?:\/\/[^)]+\)/gi, 'url(about:blank)')
    out = out.replace(/url\s*\(\s*(['"]?)\/\//gi, 'url(about:blank')
  }
  return out
}

/**
 * Park style="" / <style> as data-* placeholders BEFORE any DOM parse.
 * Chrome applies parent CSP to DOMParser docs — every newsletter style=""
 * otherwise floods the console (1000+ "style-src 'self'" errors).
 */
function toBodyFragment(html: string): string {
  let s = html.replace(/<!DOCTYPE[^>]*>/i, '')
  const headStyles = [...s.matchAll(/<style\b[^>]*>[\s\S]*?<\/style>/gi)].map((m) => m[0])
  const body = s.match(/<body\b[^>]*>([\s\S]*)<\/body>/i)
  if (body) {
    // Prefer body content; keep head <style> so newsletter CSS survives.
    const inBody = new Set(body[1].match(/<style\b[^>]*>[\s\S]*?<\/style>/gi) || [])
    const fromHead = headStyles.filter((st) => !inBody.has(st))
    return fromHead.join('') + body[1]
  }
  return s.replace(/<\/?(?:html|head|body)\b[^>]*>/gi, '')
}

function parkStyles(html: string): {
  html: string
  attrs: string[]
  blocks: string[]
} {
  const attrs: string[] = []
  const blocks: string[] = []
  let out = toBodyFragment(html)
  out = out.replace(/<script\b[\s\S]*?<\/script>/gi, '')
  out = out.replace(/<script\b[^>]*>/gi, '')
  out = out.replace(/<style\b([^>]*)>([\s\S]*?)<\/style>/gi, (_m, tagAttrs: string, css: string) => {
    const i = blocks.length
    blocks.push(css)
    return `<style data-mail-parked-style="${i}"${tagAttrs}></style>`
  })
  out = out.replace(/\sstyle\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/gi, (_m, d, s, u) => {
    const i = attrs.length
    attrs.push(d ?? s ?? u ?? '')
    return ` data-mail-parked-attr="${i}"`
  })
  return { html: out, attrs, blocks }
}

function constrainParkedInlineStyle(style: string): string {
  let next = style
    .replace(/(?:^|;)\s*width\s*:\s*\d{3,}px\s*/gi, ';')
    .replace(/(?:^|;)\s*min-width\s*:\s*\d{3,}px\s*/gi, ';min-width:0')
    .replace(/;;+/g, ';')
    .replace(/^;|;$/g, '')
    .trim()
  if (/width\s*:\s*\d{3,}px/i.test(style) && !/max-width\s*:/i.test(next)) {
    next = next ? `${next};max-width:100%` : 'max-width:100%'
  }
  return next
}

function unparkStyles(html: string, attrs: string[], blocks: string[]): string {
  let out = html.replace(/\sdata-mail-parked-attr="(\d+)"/gi, (_m, id: string) => {
    const style = attrs[Number(id)]
    if (!style) return ''
    const esc = style.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
    return ` style="${esc}"`
  })
  out = out.replace(
    /<style\b([^>]*?)\sdata-mail-parked-style="(\d+)"([^>]*)>([\s\S]*?)<\/style>/gi,
    (_m, before: string, id: string, after: string) => {
      const css = blocks[Number(id)] || ''
      return `<style${before}${after}>${css}</style>`
    },
  )
  return out
}

/** Drop parked styles entirely — safe for parent DOM under style-src 'self'. */
function dropParkedStyles(html: string): string {
  return html
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/\sdata-mail-parked-attr="\d+"/gi, '')
    .replace(/\sdata-mail-parked-attr-fallback="[^"]*"/gi, '')
}

const PURIFY_ADD_ATTR = [
  'target',
  'rel',
  'data-mail-cid',
  'data-mail-remote',
  'data-mail-remotes',
  'data-mail-blocked',
  'data-mail-parked-attr',
  'data-mail-parked-style',
] as const

export type SanitizeOptions = {
  /** Default true — strip remote http(s) media (C-22 / R-MAIL-006). */
  blockRemote?: boolean
  /**
   * Restore style="" / <style> after sanitize.
   * Default true when nginx allows style-src 'unsafe-inline' (React + newsletter).
   * Pass false only under strict CSP without unsafe-inline.
   */
  keepStyles?: boolean
}

export function sanitizeEmailHtml(dirty: string, opts: SanitizeOptions = {}): string {
  if (!dirty) return ''
  if (typeof document === 'undefined') {
    throw new Error('sanitizeEmailHtml requires a DOM (document)')
  }

  const allowRemote = opts.blockRemote === false
  const parked = parkStyles(dirty)

  // DOM without style="" / <style> bodies — no parent CSP style-src violations.
  const parsed = new DOMParser().parseFromString(
    `<!DOCTYPE html><html><body><div id="mail-sanitize-root">${parked.html}</div></body></html>`,
    'text/html',
  )
  const wrap =
    parsed.getElementById('mail-sanitize-root') || parsed.createElement('div')
  prepassBlockRemote(wrap, allowRemote)
  alsoBlockJavascriptHref(wrap)
  forceExternalLinksNewTab(wrap)
  // width= HTML attrs only (inline styles are parked).
  constrainFixedLayoutWidths(wrap)

  // Remote urls + width clamps on parked inline styles (string path, CSP-safe).
  for (const el of Array.from(wrap.querySelectorAll('[data-mail-parked-attr]'))) {
    const idx = Number(el.getAttribute('data-mail-parked-attr'))
    if (!Number.isFinite(idx) || idx < 0 || idx >= parked.attrs.length) continue
    let style = parked.attrs[idx] || ''
    if (!allowRemote) {
      const urls = extractStyleUrls(style).filter((u) => isRemoteUrl(u))
      if (urls.length) {
        for (const u of urls) markBlockedRemote(el, u)
        style = style.replace(STYLE_URL_RE, 'none')
      }
    }
    parked.attrs[idx] = constrainParkedInlineStyle(style)
  }
  for (let i = 0; i < parked.blocks.length; i++) {
    parked.blocks[i] = sanitizeCssText(parked.blocks[i] || '', allowRemote)
  }

  // Keep marked <img> alive through DOMPurify (it drops src-less images).
  for (const img of Array.from(wrap.querySelectorAll('img[data-mail-remote]'))) {
    if (!img.getAttribute('src')) {
      img.setAttribute(
        'src',
        'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
      )
    }
  }
  for (const srcEl of Array.from(wrap.querySelectorAll('source[data-mail-remote]'))) {
    if (!srcEl.getAttribute('srcset') && !srcEl.getAttribute('src')) {
      srcEl.setAttribute('data-mail-blocked', '1')
    }
  }

  const beforePurify = wrap.innerHTML
  const dp = getPurify()
  const purifyOpts = {
    USE_PROFILES: { html: true },
    FORBID_TAGS: [
      'script',
      'iframe',
      'object',
      'embed',
      'form',
      'input',
      'button',
      'textarea',
      'select',
      'base',
      'link',
      'meta',
      'svg',
      'math',
    ],
    FORBID_ATTR: ['srcdoc'],
    ALLOW_DATA_ATTR: true,
    ADD_TAGS: ['style'],
    ADD_ATTR: [...PURIFY_ADD_ATTR] as string[],
  }

  const keepStyles = opts.keepStyles !== false
  const finish = (html: string) => {
    const restored = keepStyles
      ? unparkStyles(html, parked.attrs, parked.blocks)
      : dropParkedStyles(html)
    return forceExternalLinksNewTabInHtml(restored)
  }

  const cleaned = dp.sanitize(beforePurify, purifyOpts)

  if (typeof cleaned === 'string' && cleaned.length > 0) {
    if (countBlockedRemote(cleaned) > 0 || countBlockedRemote(beforePurify) === 0) {
      return finish(cleaned)
    }
  }

  const node = dp.sanitize(beforePurify, {
    ...purifyOpts,
    RETURN_DOM: true,
  }) as unknown as HTMLElement

  const fromDom = node && typeof node.innerHTML === 'string' ? node.innerHTML : ''
  if (countBlockedRemote(fromDom) > 0) return finish(fromDom)
  return finish(beforePurify)
}

/** Count remote media URLs that were stripped (for C-22 notice). */
export function countBlockedRemote(html: string): number {
  if (!html) return 0
  const seen = new Set<string>()
  const pushList = (raw: string) => {
    const decoded = raw
      .replace(/&#10;/gi, '\n')
      .replace(/&#x0a;/gi, '\n')
      .replace(/&quot;/gi, '"')
      .replace(/&amp;/gi, '&')
    for (const u of decoded.split(/\n/).map((s) => s.trim()).filter(Boolean)) {
      seen.add(u)
    }
  }
  for (const m of html.matchAll(/\bdata-mail-remotes=(["'])([\s\S]*?)\1/gi)) {
    pushList(m[2])
  }
  for (const m of html.matchAll(/\bdata-mail-remote=(["'])([^"']+)\1/gi)) {
    seen.add(m[2])
  }
  if (seen.size === 0) {
    return (html.match(/\bdata-mail-remote=/g) || []).length
  }
  return seen.size
}

export function countCidRefs(html: string): number {
  if (!html) return 0
  return (html.match(/\bdata-mail-cid=/g) || []).length
}

export const TECH_BUZZ_FIXTURE_HTML = `
<div class="tech-buzz">
  <table width="100%" style="background-image: url(https://cdn.example.com/tech-buzz/hero-bg.png); background-color:#111">
    <tr>
      <td>
        <picture>
          <source srcset="https://cdn.example.com/tech-buzz/hero.webp 1x, https://cdn.example.com/tech-buzz/hero@2x.webp 2x" type="image/webp" />
          <img
            src="https://cdn.example.com/tech-buzz/hero.jpg"
            srcset="https://cdn.example.com/tech-buzz/hero-1x.jpg 1x, https://cdn.example.com/tech-buzz/hero-2x.jpg 2x"
            alt="The Tech Buzz"
          />
        </picture>
        <img data-src="https://cdn.example.com/tech-buzz/tracker.gif" alt="" width="1" height="1" />
        <a href="https://example.com"><img src="//cdn.example.com/tech-buzz/logo.png" alt="logo" /></a>
      </td>
    </tr>
  </table>
</div>
`.trim()

export function textToSafeHtml(text: string): string {
  const esc = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
  return esc
    .split(/\n{2,}/)
    .map((p) => `<p>${p.replace(/\n/g, '<br/>')}</p>`)
    .join('')
}

export function translationLooksGarbage(original: string, translated: string | null | undefined): boolean {
  if (translated == null) return false
  const out = translated.trim()
  if (!out) return true
  if (out.length < 8 && (original || '').trim().length > 40) return true
  if (/(?:=[0-9A-Fa-f]{2}){8,}/.test(out)) return true
  if (/(?:1f){12,}/i.test(out)) return true
  if ((out.match(/=/g) || []).length > Math.max(12, out.length / 8)) return true
  if (/(?:va-[\w-]+|text-align|font-family)\s*:/i.test(out) && (out.match(/:/g) || []).length > 6) {
    return true
  }
  const letters = [...out].filter((c) => /\p{L}/u.test(c)).length
  if (out.length > 80 && letters / out.length < 0.35) return true
  return false
}
